/*
 * The privacy policy's file format, and nothing else.
 *
 * This module is shared by two very different readers: the app (through
 * privacy.ts, which bundles the file with Vite's `?raw`) and the build itself
 * (vite.config.ts, which writes `/privacy.html` from the same file). So it must
 * import nothing — no `?raw`, no `import.meta.env`, no DOM, no React — or the
 * Vite config could not load it. Keeping the parse here rather than duplicating
 * a header regex in the plugin means the page a store reviewer reads and the
 * sheet a player reads cannot disagree about which version is in effect.
 *
 * The format is the changelog's idea applied to one document: no front matter,
 * just a title and a few `Key: value` lines the app reads.
 *
 *     # Privacy Policy
 *
 *     Version: 3
 *     MaterialVersion: 2
 *     Effective: 2026-10-15
 *     Status: final
 *
 *     ...the policy...
 *
 *     ## What changed in this version
 *     - One line per change
 *
 * Like changelog.ts this never throws: a malformed file must not take the home
 * screen down with it. It reports what it could not read in `problems`, and
 * privacy.test.ts — which parses the REAL file — fails on any of them. The
 * fallbacks are chosen to be the safe reading: an unreadable status is a draft,
 * because "final" is what lets a production build show the policy.
 */

/**
 * The section the change list lives in. Deliberately NOT `## Changes`: the
 * policy has its own section by that name, addressed to the reader ("If we
 * change what we collect..."), and it is part of the text. See privacy.ts.
 */
export const CHANGE_LIST_HEADING = "What changed in this version";

export type PrivacyStatus = "draft" | "final";

export interface PrivacyPolicy {
  /** Moves on every edit. 0 when unreadable. */
  version: number;
  /** Moves only on a material change; never above `version`. 0 when unreadable. */
  materialVersion: number;
  /** `YYYY-MM-DD`, or "" when unreadable. */
  effective: string;
  /** "draft" when unreadable — see the module note. */
  status: PrivacyStatus;
  /** The policy text: everything after the header, minus the change list. */
  body: string;
  /** One entry per bullet under {@link CHANGE_LIST_HEADING}; empty for version 1. */
  changes: string[];
  /** Everything wrong with the file. Empty for a well-formed one. */
  problems: string[];
}

const TITLE = /^#\s+Privacy Policy\s*$/;
const FIELD = /^([A-Za-z]+):\s*(.*?)\s*$/;
const CHANGE_LIST = new RegExp(`^##\\s+${CHANGE_LIST_HEADING}\\s*$`, "i");
const BULLET = /^[-*]\s+(.+?)\s*$/;
const HEADING = /^(#{1,3})\s+(.+?)\s*$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const KNOWN_FIELDS = ["Version", "MaterialVersion", "Effective", "Status"] as const;

/**
 * `[operator name, address]` — a placeholder left for legal review. A markdown
 * link's text, `[text](url)`, is not one, hence the lookahead.
 */
const PLACEHOLDER = /\[[^\]]+\](?!\()/g;

/** Every `[bracketed placeholder]` still in the text, in order. */
export function findPlaceholders(text: string): string[] {
  return [...String(text ?? "").matchAll(PLACEHOLDER)].map((m) => m[0].replace(/\s+/g, " "));
}

function positiveInt(value: string | undefined): number | null {
  return value !== undefined && /^\d+$/.test(value) && Number(value) >= 1 ? Number(value) : null;
}

/** A real calendar date, not merely the right shape: `2026-02-30` is refused. */
function isCalendarDate(value: string): boolean {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** Drops blank lines from both ends. */
function trimBlankLines(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && !lines[start].trim()) start++;
  while (end > start && !lines[end - 1].trim()) end--;
  return lines.slice(start, end);
}

export function parsePrivacy(text: string): PrivacyPolicy {
  const problems: string[] = [];
  const lines = String(text ?? "").split(/\r?\n/);
  let i = 0;

  while (i < lines.length && !lines[i].trim()) i++;
  if (i < lines.length && TITLE.test(lines[i])) i++;
  else problems.push('The file must start with "# Privacy Policy".');
  while (i < lines.length && !lines[i].trim()) i++;

  // The header is the run of `Key: value` lines straight after the title. It
  // ends at the first line that is not one, which is the blank before the text.
  const fields = new Map<string, string>();
  for (; i < lines.length; i++) {
    const field = FIELD.exec(lines[i]);
    if (!field) break;
    const [, key, value] = field;
    if (!(KNOWN_FIELDS as readonly string[]).includes(key)) problems.push(`Unknown header field "${key}".`);
    else if (fields.has(key)) problems.push(`Header field "${key}" is given twice.`);
    else fields.set(key, value);
  }
  for (const key of KNOWN_FIELDS) {
    if (!fields.has(key)) problems.push(`Header field "${key}" is missing.`);
  }

  const version = positiveInt(fields.get("Version"));
  if (fields.has("Version") && version === null) problems.push(`Version "${fields.get("Version")}" is not a whole number of 1 or more.`);
  const materialVersion = positiveInt(fields.get("MaterialVersion"));
  if (fields.has("MaterialVersion") && materialVersion === null) {
    problems.push(`MaterialVersion "${fields.get("MaterialVersion")}" is not a whole number of 1 or more.`);
  }
  // Every material change is a change, so it moves Version too.
  if (version !== null && materialVersion !== null && materialVersion > version) {
    problems.push(`MaterialVersion ${materialVersion} is above Version ${version}.`);
  }

  const effectiveRaw = fields.get("Effective") ?? "";
  const effective = isCalendarDate(effectiveRaw) ? effectiveRaw : "";
  if (fields.has("Effective") && !effective) problems.push(`Effective "${effectiveRaw}" is not a YYYY-MM-DD date.`);

  const statusRaw = fields.get("Status");
  const status: PrivacyStatus = statusRaw === "final" ? "final" : "draft";
  if (statusRaw !== undefined && statusRaw !== "draft" && statusRaw !== "final") {
    problems.push(`Status "${statusRaw}" must be "draft" or "final".`);
  }

  // The change list runs to the end of the file. Anything in it that is not a
  // bullet is a problem rather than more list: it is most likely policy text
  // written below the heading by mistake, and silently dropping that from the
  // policy would be the worst way for it to go wrong.
  const rest = lines.slice(i);
  const changesAt = rest.findIndex((line) => CHANGE_LIST.test(line));
  const bodyLines = changesAt === -1 ? rest : rest.slice(0, changesAt);
  const changes: string[] = [];
  if (changesAt !== -1) {
    for (const line of rest.slice(changesAt + 1)) {
      const bullet = BULLET.exec(line);
      if (bullet) changes.push(bullet[1]);
      else if (/^\s+\S/.test(line) && changes.length) changes[changes.length - 1] += ` ${line.trim()}`;
      else if (line.trim()) problems.push(`"${line.trim()}" under "${CHANGE_LIST_HEADING}" is not a bullet.`);
    }
  }

  const body = trimBlankLines(bodyLines).join("\n");
  if (!body) problems.push("The policy has no text.");

  if (status === "final") {
    const left = findPlaceholders(`${body}\n${changes.join("\n")}`);
    if (left.length) problems.push(`Status is final but placeholders remain: ${left.join(", ")}.`);
  }

  return {
    version: version ?? 0,
    materialVersion: materialVersion ?? 0,
    effective,
    status,
    body,
    changes,
    problems,
  };
}

/** The markdown the policy is written in, as blocks to draw. */
export type PolicyBlock =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

/**
 * The policy body as headings, paragraphs and bullet lists — the whole of the
 * markdown it uses. Hand-rolled for the reason the changelog has no markdown
 * library (see the project's CLAUDE.md): the subset is tiny, and a library
 * would cost more than it renders. Anything else (a `>` quote, a table) comes
 * through as paragraph text, readable if not pretty.
 *
 * Lines are joined with a space, so a `**bold**` span that wraps across lines
 * in the file still pairs up.
 */
export function policyBlocks(body: string): PolicyBlock[] {
  const blocks: PolicyBlock[] = [];
  let paragraph: { kind: "paragraph"; text: string } | null = null;
  let list: { kind: "list"; items: string[] } | null = null;

  for (const line of String(body ?? "").split(/\r?\n/)) {
    if (!line.trim()) {
      paragraph = null;
      list = null;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      paragraph = null;
      list = null;
      blocks.push({ kind: "heading", level: heading[1].length as 1 | 2 | 3, text: heading[2] });
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      paragraph = null;
      if (!list) {
        list = { kind: "list", items: [] };
        blocks.push(list);
      }
      list.items.push(bullet[1]);
      continue;
    }
    // An indented line inside a list is the bullet above it, wrapped.
    if (list && /^\s/.test(line)) {
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    list = null;
    if (paragraph) paragraph.text += ` ${line.trim()}`;
    else {
      paragraph = { kind: "paragraph", text: line.trim() };
      blocks.push(paragraph);
    }
  }
  return blocks;
}

/**
 * `**bold**` spans, split out — the same convention, and the same pattern, the
 * tutorial coach and rules sheet use. Plain runs come back with `bold: false`.
 */
export function boldRuns(text: string): { text: string; bold: boolean }[] {
  return text
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((run) => run.text.length > 0);
}

/**
 * `2026-10-01` as the reader's locale writes it, built as a LOCAL date so a
 * timezone behind UTC does not print the day before.
 *
 * The same few lines as changelog.ts's formatReleaseDate, copied on purpose:
 * importing that module would pull the whole bundled CHANGELOG.md into
 * whichever chunk imports this one, and this one is also loaded by the Vite
 * config, which cannot import `?raw` at all.
 */
export function formatPolicyDate(iso: string, locale?: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}
